import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits, getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import { useSiteTakeoverCampaignUIState } from 'takeovers/utils/hooks/useSiteTakeoverCampaignUIState'
import { LUX_PLUS_MONTH_CAMPAIGN_ID } from '../constants/luxPlusMonthTakeoverConstants'

interface LuxPlusMonthOfferState {
  // Whether the LuxPlus+ Month campaign tag pill is shown for this offer
  showsCampaignTag: boolean
  // Whether this offer has any LuxPlus+ access restriction at all (early access or member-only) —
  // false for offers that just happen to carry the campaign tag but are open to everyone.
  requiresLuxPlusAccess: boolean
  // Whether the viewer still needs to join LuxPlus+ to access this specific offer
  membershipRequired: boolean
}

export function useLuxPlusMonthOfferState(offer?: App.AnyOffer): LuxPlusMonthOfferState {
  const isFreePreviewLuxPlusMember = useAppSelector(getIsFreePreviewLuxPlusMember)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  // Only true when the campaign tag pill itself is shown for this campaign
  const { shouldShowTag } = useSiteTakeoverCampaignUIState({ campaignTagId: LUX_PLUS_MONTH_CAMPAIGN_ID })

  const hasCampaignTag = !!offer?.campaignTags?.includes(LUX_PLUS_MONTH_CAMPAIGN_ID)
  const showsCampaignTag = hasCampaignTag && shouldShowTag

  const hasEarlyAccess = offer?.luxPlus?.access === 'earlyAccess'
  const isMemberOnly = offer?.luxPlus?.access === 'memberOnly'
  const requiresLuxPlusAccess = hasEarlyAccess || isMemberOnly
  const canFreePreviewBuyMemberOnly = isFreePreviewLuxPlusMember && offer?.luxPlus?.restrictPurchaseToMembers === false
  const memberOnlyMembershipRequired = isMemberOnly && !canFreePreviewBuyMemberOnly
  // A full paying member already has access regardless of the offer's access/memberOnly tier.
  const membershipRequired = !canRedeemLuxPlusBenefits && (hasEarlyAccess || memberOnlyMembershipRequired)

  return { showsCampaignTag, requiresLuxPlusAccess, membershipRequired }
}
